import {
  Building,
  DollarSign,
  FileText,
  Plus,
  TrendingUp,
  Users,
} from 'lucide-react';
// src/pages/DashboardPage.tsx
import type React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';

import AssetForm from '@/components/assets/AssetForm';
import { CustomizableKpiDashboard } from '@/components/dashboard/CustomizableKpiDashboard';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import AssetService from '../services/AssetService';
import KpiPreferencesService from '../services/KpiPreferencesService';
import type { Asset } from '../types/asset';
import type { PortfolioKpiData } from '../types/kpi';

const DashboardPage: React.FC = () => {
  const [allAssets, setAllAssets] = useState<Asset[]>([]);
  const [portfolioKpiData, setPortfolioKpiData] =
    useState<PortfolioKpiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAddAssetDrawerOpen, setIsAddAssetDrawerOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [assets, kpiData] = await Promise.all([
          AssetService.getAssets(),
          KpiPreferencesService.calculatePortfolioKpis(),
        ]);
        setAllAssets(assets);
        setPortfolioKpiData(kpiData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddAssetSuccess = () => {
    // Refresh the asset list and KPI data
    Promise.all([
      AssetService.getAssets(),
      KpiPreferencesService.calculatePortfolioKpis(),
    ]).then(([assets, kpiData]) => {
      setAllAssets(assets);
      setPortfolioKpiData(kpiData);
    });
  };

  const calculateTotalValue = () => {
    if (allAssets.length === 0) return 0;
    return allAssets.reduce((sum, asset) => {
      // Use market value if available, otherwise fall back to purchase price
      const portfolioValue = Number(
        asset.market_value || asset.purchase_price || 0,
      );
      return sum + portfolioValue;
    }, 0);
  };

  return (
    <div className="container mx-auto">
      {/* Unified Customizable KPI Dashboard */}
      <CustomizableKpiDashboard
        scope="dashboard"
        portfolioData={portfolioKpiData || undefined}
        onRefresh={handleAddAssetSuccess}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Recent Assets</CardTitle>
              <Link to="/app/assets">
                <Button variant="ghost" size="sm">
                  View All
                </Button>
              </Link>
            </div>
            <CardDescription>
              Your most recently added properties
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-4">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="h-12 bg-gray-200 rounded" />
                ))}
              </div>
            ) : allAssets.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No assets found</p>
                <Button
                  size="sm"
                  className="mt-2"
                  onClick={() => setIsAddAssetDrawerOpen(true)}
                >
                  Add Your First Asset
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {allAssets.slice(0, 3).map((asset) => (
                  <Link key={asset.id} to={`/app/assets/${asset.slug}`}>
                    <div className="flex items-center p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <Building className="h-10 w-10 p-2 bg-blue-100 text-blue-600 rounded-full mr-4" />
                      <div>
                        <h4 className="font-medium">{asset.name}</h4>
                        <p className="text-sm text-gray-500">
                          {asset.address || asset.asset_type}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              Common tasks to manage your properties
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="h-auto py-4 justify-start"
                onClick={() => setIsAddAssetDrawerOpen(true)}
              >
                <div className="flex flex-col items-start text-left">
                  <div className="flex items-center">
                    <Building className="h-4 w-4 mr-2" />
                    <span className="font-medium">Add Asset</span>
                  </div>
                  <span className="text-xs text-gray-500 mt-1">
                    Record a new property
                  </span>
                </div>
              </Button>

              <Link to="/contacts">
                <Button
                  variant="outline"
                  className="h-auto py-4 justify-start w-full"
                >
                  <div className="flex flex-col items-start text-left">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      <span className="font-medium">Manage Contacts</span>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">
                      Add tenants, contractors, etc.
                    </span>
                  </div>
                </Button>
              </Link>

              <Link to="/documents">
                <Button
                  variant="outline"
                  className="h-auto py-4 justify-start w-full"
                >
                  <div className="flex flex-col items-start text-left">
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      <span className="font-medium">Upload Documents</span>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">
                      Store important files
                    </span>
                  </div>
                </Button>
              </Link>

              <Button
                variant="outline"
                className="h-auto py-4 justify-start"
                disabled
              >
                <div className="flex flex-col items-start text-left">
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-2" />
                    <span className="font-medium">Track Finances</span>
                  </div>
                  <span className="text-xs text-gray-500 mt-1">
                    Coming soon
                  </span>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <AssetForm
        open={isAddAssetDrawerOpen}
        onOpenChange={setIsAddAssetDrawerOpen}
        onAssetAdded={handleAddAssetSuccess}
      />
    </div>
  );
};

export default DashboardPage;
